import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authService } from '@services/authService';
import type { User } from '../types/api';

interface AuthStore {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (data: {
    username: string;
    email: string;
    password: string;
    confirm_password: string;
    invite_code?: string;
    age?: string;
    first_name?: string;
    last_name?: string;
  }) => Promise<void>;
  clearError: () => void;
  updateUser: (userData: Partial<User>) => void;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (username, password) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authService.login(username, password);
          const backendUser = response.user as any;
          set({
            user: {
              ...response.user,
              level: backendUser.user_level ?? response.user.level ?? 1,
              level_name: backendUser.level_name ?? '',
              total_xp: backendUser.total_xp ?? 0,
            },
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (error: any) {
          // 提取和格式化错误信息
          let errorMessage = '登录失败，请重试';
          
          if (error.response?.data) {
            const errorData = error.response.data;
            
            // 优先显示具体的字段错误
            if (errorData.errors && typeof errorData.errors === 'object') {
              const errorList = [];
              for (const [field, messages] of Object.entries(errorData.errors)) {
                if (Array.isArray(messages)) {
                  errorList.push(...messages.map(msg => `${field}: ${msg}`));
                } else {
                  errorList.push(`${field}: ${messages}`);
                }
              }
              if (errorList.length > 0) {
                errorMessage = errorList.join('; ');
              } else if (errorData.message) {
                errorMessage = errorData.message;
              }
            } else if (errorData.detail || errorData.message) {
              errorMessage = errorData.detail || errorData.message;
            } else if (typeof errorData === 'string') {
              errorMessage = errorData;
            }
          } else if (error.message) {
            errorMessage = error.message;
          }

          set({
            error: errorMessage,
            isLoading: false,
          });
          throw error;
        }
      },

      register: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authService.register(data);
          set({
            user: response.user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (error: any) {
          // 提取和格式化错误信息
          let errorMessage = '注册失败，请重试';
          
          if (error.response?.data) {
            const errorData = error.response.data;
            
            // 优先显示具体的字段错误
            if (errorData.errors && typeof errorData.errors === 'object') {
              const errorList = [];
              for (const [field, messages] of Object.entries(errorData.errors)) {
                if (Array.isArray(messages)) {
                  errorList.push(...messages.map(msg => `${field}: ${msg}`));
                } else {
                  errorList.push(`${field}: ${messages}`);
                }
              }
              if (errorList.length > 0) {
                errorMessage = errorList.join('; ');
              } else if (errorData.message) {
                errorMessage = errorData.message;
              }
            } else if (errorData.message) {
              errorMessage = errorData.message;
            } else if (typeof errorData === 'string') {
              errorMessage = errorData;
            }
          } else if (error.message) {
            errorMessage = error.message;
          }

          set({
            error: errorMessage,
            isLoading: false,
          });
          throw error;
        }
      },

      logout: async () => {
        set({ isLoading: true });
        try {
          await authService.logout();
        } finally {
          // 无论是否成功，都清除本地状态
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
        }
      },

      clearError: () => set({ error: null }),

      updateUser: (userData) => {
        const currentUser = get().user;
        if (currentUser) {
          set({ user: { ...currentUser, ...userData } });
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
